/**
 * Product Media Zoom Component - Adds click-to-zoom functionality to product images
 * Click to zoom in, click again to zoom out. Mouse movement pans while zoomed.
 */

import { Component } from './component-base.js';

/**
 * Custom element for product image zoom on click
 * @extends Component
 */
export class ProductMediaZoom extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Configuration
    this.zoomScale = this.dataset.zoomScale || '1.5';
    this.transitionDuration = this.dataset.transitionDuration || '300ms';

    // Track zoom state for each image
    this.zoomedImages = new WeakMap();

    // Get all zoomable images
    this.images = this.querySelectorAll('[data-zoomable]');
    
    if (!this.images.length) {
      console.warn('[ProductMediaZoom] No zoomable images found');
      return;
    }

    this.#initializeImages();
    this.#attachListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detachListeners();
  }

  /**
   * Sets up initial styles for zoomable images
   */
  #initializeImages() {
    this.images.forEach((img) => {
      img.style.transition = `transform ${this.transitionDuration} ease-out`;
      img.style.transformOrigin = 'center center';
      img.style.cursor = 'zoom-in';
      this.zoomedImages.set(img, false);
    });
  }

  /**
   * Attaches mouse event listeners to images
   */
  #attachListeners() {
    this.images.forEach((img) => {
      img.addEventListener('click', this.#handleClick);
      img.addEventListener('mousemove', this.#handleMouseMove);
    });
  }

  /**
   * Removes event listeners on cleanup
   */
  #detachListeners() {
    this.images.forEach((img) => {
      img.removeEventListener('click', this.#handleClick);
      img.removeEventListener('mousemove', this.#handleMouseMove);
    });
  }

  /**
   * Handles click - toggles zoom on/off
   */
  #handleClick = (e) => {
    const img = e.currentTarget;
    if (!img) return;

    const isZoomed = this.zoomedImages.get(img);
    
    if (isZoomed) {
      // Zoom out
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
      img.style.cursor = 'zoom-in';
      this.zoomedImages.set(img, false);
    } else {
      // Zoom in
      img.style.transform = `scale(${this.zoomScale})`;
      img.style.cursor = 'zoom-out';
      this.zoomedImages.set(img, true);
      
      // Set initial transform origin based on click position
      const rect = img.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = `${x}% ${y}%`;
    }
  };

  /**
   * Handles mouse move - adjusts transform origin for smooth panning when zoomed
   */
  #handleMouseMove = (e) => {
    const img = e.currentTarget;
    if (!img) return;

    const isZoomed = this.zoomedImages.get(img);
    if (!isZoomed) return; // Only pan when zoomed

    const rect = img.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    img.style.transformOrigin = `${x}% ${y}%`;
  };
}

customElements.define('product-media-zoom', ProductMediaZoom);

